import React, { Component, ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui";
import i18n from "@/shared/lib/i18n";

interface ErrorBoundaryProps {
    children: ReactNode;
    fallback?: ReactNode;
    onReset?: () => void;
    section?: string;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        this.setState({ errorInfo });
        // Log to error reporting service
        console.error("ErrorBoundary caught an error:", error, errorInfo);
        // Report to Sentry (caught errors are not sent by default)
        Sentry.captureException(error, {
            contexts: {
                react: {
                    componentStack: errorInfo.componentStack,
                },
            },
        });
    }

    handleReset = (): void => {
        this.setState({ hasError: false, error: null, errorInfo: null });
        this.props.onReset?.();
    };

    handleGoHome = (): void => {
        window.location.href = "/";
    };

    render(): ReactNode {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="flex items-center justify-center min-h-[400px] p-4">
                    <Card className="max-w-md w-full border-red-200 dark:border-red-900/50 bg-red-50/50 dark:bg-red-950/20">
                        <CardHeader className="text-center">
                            <div className="mx-auto w-12 h-12 bg-red-100 dark:bg-red-950/50 rounded-full flex items-center justify-center mb-4">
                                <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
                            </div>
                            <CardTitle className="text-red-900 dark:text-red-300">
                                {this.props.section
                                    ? i18n.t("shared:errorBoundary.errorInSection", { section: this.props.section })
                                    : i18n.t("shared:errorBoundary.somethingWentWrong")}
                            </CardTitle>
                            <CardDescription className="text-red-700 dark:text-red-400">
                                {i18n.t("shared:errorBoundary.unexpectedError")}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {process.env.NODE_ENV === "development" && this.state.error && (
                                <div className="p-3 bg-red-100 dark:bg-red-950/40 rounded-md text-sm text-red-800 dark:text-red-300 font-mono overflow-auto max-h-32">
                                    {this.state.error.message}
                                </div>
                            )}
                            <div className="flex gap-2 justify-center">
                                <Button
                                    variant="outline"
                                    onClick={this.handleReset}
                                    className="border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-950/40"
                                >
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                    {i18n.t("shared:errorBoundary.tryAgain")}
                                </Button>
                                <Button
                                    variant="default"
                                    onClick={this.handleGoHome}
                                    className="bg-red-600 hover:bg-red-700"
                                >
                                    <Home className="h-4 w-4 mr-2" />
                                    {i18n.t("shared:errorBoundary.homePage")}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            );
        }

        return this.props.children;
    }
}

// Hook-friendly wrapper for functional components
export function withErrorBoundary<P extends object>(
    WrappedComponent: React.ComponentType<P>,
    section?: string
): React.FC<P> {
    return function WithErrorBoundary(props: P) {
        return (
            <ErrorBoundary section={section}>
                <WrappedComponent {...props} />
            </ErrorBoundary>
        );
    };
}
